import DetailMixinController from '@/mixins/DetailMixinController';
import NetworkService from '@/services/NetworkService';
import UserService from '@/services/UserService';

const subscriptionStatus = {
  incomplete: 'Problema de pagamento',
  incomplete_expired: 'Cancelado (expirado)',
  trialing: 'Período de teste',
  active: 'Ativo',
  past_due: 'Pagamento atrasado',
  canceled: 'Cancelado',
  unpaid: 'Não pago',
  paused: 'Pausado',
};

const periodLabel = {
  month: 'Mensal',
  semester: 'Semestral',
  year: 'Anual',
};

const invoiceStatus = {
  uncollectible: 'Cobrança não aplicável',
  draft: 'Rascunho',
  open: 'Em aberto',
  void: 'Cancelado',
  paid: 'Pago',
};

function translateInvoiceDescription(desc) {
  if (!desc) return desc;
  let out = desc
    .replace(/\(at /gi, '(')
    .replace(/ \/ year\)/gi, ' / ano)')
    .replace(/ \/ month\)/gi, ' / mês)')
    .replace(/ \/ week\)/gi, ' / semana)')
    .replace(/ \/ day\)/gi, ' / dia)')
    .replace(/ \/ semester\)/gi, ' / semestre)')
    .replace(/Time\s?-\s?off/gi, 'Período sem cobrança')
    .replace(/Unused time/gi, 'Tempo não utilizado')
    .replace(/Remaining time/gi, 'Tempo restante')
    .replace(/Trial period/gi, 'Período de teste')
    .replace(/ on /gi, ' em ');

  out = out.replace(
    /R\$\s?(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?)/g,
    (match, num) => {
      const [intPart, decPart] = num.split('.');
      const intFormatted = intPart.replace(/,/g, '.');
      return decPart ? `R$ ${intFormatted},${decPart}` : `R$ ${intFormatted}`;
    }
  );

  return out;
}

export default {
  name: 'SubscriptionDetail',
  mixins: [DetailMixinController],
  data() {
    return {
      network: new NetworkService(),
      planLabels: new UserService().planLabels,
      // Modals
      showGrantDaysModal: false,
      showGrantFreeMonthModal: false,
      showGrantCreditModal: false,
      // Forms
      grantDaysForm: {
        days: 7,
      },
      grantFreeMonthForm: {
        duration_in_months: 1,
      },
      grantCreditForm: {
        amount: null,
        description: '',
      },
      // Submitting states
      submittingGrantDays: false,
      submittingGrantFreeMonth: false,
      submittingGrantCredit: false,
    };
  },
  computed: {
    isPlusPlan() {
      return this.item.plan?.slug === 'psiapp-mais-pro';
    },
    canGrantDays() {
      return ['active', 'trialing'].includes(this.item.status);
    },
    canGrantFreeMonth() {
      return ['active', 'trialing', 'past_due'].includes(this.item.status);
    },
    canGrantCredit() {
      return this.item.status === 'active' && this.item.user?.customer_id;
    },
    creditExceedsSubscription() {
      if (!this.grantCreditForm.amount || !this.item.unit_amount) return false;
      const creditInCents = this.grantCreditForm.amount * 100;
      return creditInCents > this.item.unit_amount;
    },
  },
  methods: {
    fetchPromise() {
      return this.network.get(`api/subscriptions/${this.id}`);
    },
    prepareResponse(data) {
      // Format status
      let formatted_status = subscriptionStatus[data.status] || data.status || '---';
      if (
        data.status === 'canceled' &&
        data.trial_end &&
        this.$moment(data.trial_end).isAfter(this.$moment())
      ) {
        formatted_status = `Cortesia até ${this.$moment(data.trial_end).format('DD/MM/YYYY')}`;
      } else if (data.status === 'active' && data.canceled_at) {
        formatted_status = `Ativo até ${this.$moment(data.cancel_at).format('DD/MM/YYYY')}`;
      } else if (data.status === 'trialing' && data.trial_end) {
        formatted_status = `Período de teste até ${this.$moment(data.trial_end).format('DD/MM/YYYY')}`;
      }

      // Format plan frequency
      let formatted_plan_frequency = '---';
      if (data.plan?.slug && this.planLabels[data.plan.slug]) {
        const planName = this.planLabels[data.plan.slug];
        const frequency = data.price?.frequency ? ` ${periodLabel[data.price.frequency]}` : '';
        formatted_plan_frequency = `${planName}${frequency}`;
      }

      // Format invoices (API returns all_invoices)
      let invoices = data.all_invoices || data.invoices || [];
      invoices = invoices.map((inv) => ({
        ...inv,
        formatted_created: inv.created
          ? this.$moment(inv.created).format('DD/MM/YYYY')
          : '-',
        formatted_status: inv.status ? invoiceStatus[inv.status] : '',
        formatted_total: inv.total != null
          ? (inv.total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          : '-',
      }));

      // Format upcoming invoice
      let upcoming_invoice = null;
      if (data.upcoming_invoice) {
        const ui = data.upcoming_invoice;
        const currency = (ui.currency || 'brl').toUpperCase() === 'BRL' ? 'BRL' : 'USD';
        const fmt = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency });

        const lines = (ui.lines || []).map((l) => ({
          ...l,
          description: translateInvoiceDescription(l.description),
          is_credit: (l.amount || 0) < 0,
          formatted_amount: fmt(l.amount),
        }));
        const credits = (ui.credits || []).map((c) => ({
          ...c,
          description: translateInvoiceDescription(c.description),
          formatted_amount: fmt(c.amount),
        }));

        upcoming_invoice = {
          ...ui,
          lines,
          credits,
          formatted_date: ui.next_payment_attempt
            ? this.$moment(ui.next_payment_attempt).format('DD/MM/YYYY')
            : (ui.period_end ? this.$moment(ui.period_end).format('DD/MM/YYYY') : '-'),
          formatted_amount: fmt(ui.amount_due),
          formatted_subtotal: fmt(ui.subtotal),
          formatted_total: fmt(ui.total),
        };
      }

      return {
        ...data,
        formatted_status,
        formatted_plan_frequency,
        invoices,
        upcoming_invoice,
        formatted_start_date: data.start_date
          ? this.$moment(data.start_date).format('DD/MM/YYYY')
          : '-',
        formatted_canceled_at: data.canceled_at
          ? this.$moment(data.canceled_at).format('DD/MM/YYYY')
          : '---',
        formatted_current_period_start: data.current_period_start
          ? this.$moment(data.current_period_start).format('DD/MM/YYYY')
          : '-',
        formatted_current_period_end: data.current_period_end
          ? this.$moment(data.current_period_end).format('DD/MM/YYYY')
          : '-',
        formatted_created_at: data.created_at
          ? this.$moment(data.created_at).format('DD/MM/YYYY HH:mm')
          : '-',
        formatted_unit_amount: data.unit_amount
          ? (data.unit_amount / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          : '-',
      };
    },
    stripeSubscriptionLink() {
      const acctId = process.env.VUE_APP_STRIPE_PLATFORM_ACCOUNT_ID;
      const mid = process.env.VUE_APP_ENV === 'prod' ? 'subscriptions' : 'test/subscriptions';
      return `https://dashboard.stripe.com/${acctId}/${mid}/${this.item.subscription_id}`;
    },
    getInvoiceLink(invoice) {
      if (invoice.hosted_invoice_url) {
        return invoice.hosted_invoice_url;
      }
      const acctId = process.env.VUE_APP_STRIPE_PLATFORM_ACCOUNT_ID;
      const mid = process.env.VUE_APP_ENV === 'prod' ? 'invoices' : 'test/invoices';
      return `https://dashboard.stripe.com/${acctId}/${mid}/${invoice.invoice_id}`;
    },
    getCustomerStripeLink(customerId) {
      const acctId = process.env.VUE_APP_STRIPE_PLATFORM_ACCOUNT_ID;
      const mid = process.env.VUE_APP_ENV === 'prod' ? 'customers' : 'test/customers';
      return `https://dashboard.stripe.com/${acctId}/${mid}/${customerId}`;
    },
    statusBadgeClass() {
      const status = this.item.status;
      if (
        status === 'canceled' &&
        this.item.trial_end &&
        this.$moment(this.item.trial_end).isAfter(this.$moment())
      ) {
        return 'bg-primary';
      }
      if (status === 'active' && this.item.canceled_at) {
        return 'bg-warning text-dark';
      }
      const classes = {
        active: 'bg-success',
        trialing: 'bg-primary',
        canceled: 'bg-danger',
        past_due: 'bg-warning text-dark',
        unpaid: 'bg-warning text-dark',
        paused: 'bg-secondary',
        incomplete: 'bg-warning text-dark',
        incomplete_expired: 'bg-danger',
      };
      return classes[status] || 'bg-secondary';
    },
    // Modal helpers
    closeGrantDaysModal() {
      this.showGrantDaysModal = false;
      this.grantDaysForm.days = 7;
    },
    closeGrantFreeMonthModal() {
      this.showGrantFreeMonthModal = false;
      this.grantFreeMonthForm.duration_in_months = 1;
    },
    closeGrantCreditModal() {
      this.showGrantCreditModal = false;
      this.grantCreditForm.amount = null;
      this.grantCreditForm.description = '';
    },
    getNewTrialEndDate(days) {
      const baseDate = this.item.trial_end
        ? this.$moment(this.item.trial_end).clone()
        : this.$moment();
      return baseDate.add(days, 'days').format('DD/MM/YYYY');
    },
    formatCurrency(value) {
      return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    // API calls
    async handleGrantDays() {
      this.submittingGrantDays = true;
      try {
        await this.network.post(
          `api/subscriptions/${this.item.id}/grant-days`,
          { days: this.grantDaysForm.days }
        );
        this.$eventBus.$emit('showToast', {
          title: 'Dias grátis concedidos',
          msg: `${this.grantDaysForm.days} dias grátis foram adicionados à assinatura.`,
        });
        this.closeGrantDaysModal();
        this.getData();
      } catch (error) {
        this.$eventBus.$emit('showToast', {
          title: 'Erro',
          msg: error.message || 'Erro ao conceder dias grátis.',
        });
      } finally {
        this.submittingGrantDays = false;
      }
    },
    async handleGrantFreeMonth() {
      this.submittingGrantFreeMonth = true;
      try {
        await this.network.post(
          `api/subscriptions/${this.item.id}/grant-free-month`,
          { duration_in_months: this.grantFreeMonthForm.duration_in_months }
        );
        const months = this.grantFreeMonthForm.duration_in_months;
        this.$eventBus.$emit('showToast', {
          title: 'Mês grátis concedido',
          msg: `${months} ${months > 1 ? 'meses grátis foram aplicados' : 'mês grátis foi aplicado'} à assinatura.`,
        });
        this.closeGrantFreeMonthModal();
        this.getData();
      } catch (error) {
        this.$eventBus.$emit('showToast', {
          title: 'Erro',
          msg: error.message || 'Erro ao conceder mês grátis.',
        });
      } finally {
        this.submittingGrantFreeMonth = false;
      }
    },
    async handleGrantCredit() {
      this.submittingGrantCredit = true;
      try {
        const amountInCents = Math.round(this.grantCreditForm.amount * 100);
        await this.network.post(
          `api/users/${this.item.user_id}/grant-credit`,
          {
            amount: amountInCents,
            currency: 'brl',
            description: this.grantCreditForm.description || undefined,
          }
        );
        this.$eventBus.$emit('showToast', {
          title: 'Crédito concedido',
          msg: `Crédito de ${this.formatCurrency(this.grantCreditForm.amount)} foi adicionado para ${this.item.user?.name}.`,
        });
        this.closeGrantCreditModal();
        this.getData();
      } catch (error) {
        this.$eventBus.$emit('showToast', {
          title: 'Erro',
          msg: error.message || 'Erro ao conceder crédito.',
        });
      } finally {
        this.submittingGrantCredit = false;
      }
    },
  },
};
